import React, { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from './Dashboard';
import { useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { 
  Upload, FileText, Download, Trash2, Share2, MoreVertical, 
  Lock, Search, Filter, X, Users, Eye, Edit, AlertCircle,
  FolderPlus, Folder, FolderOpen, ArrowLeft, PenTool, Move, LayoutTemplate, Eraser, Clock
} from 'lucide-react';
import { toast } from 'sonner';
import DocumentPreview from '../components/DocumentPreview';
import { SignatureDialog, DocumentSignerDialog, SignatureDisplay } from '../components/SignaturePad';
import SignatureFieldEditor from '../components/SignatureFieldEditor';
import SigningAuditDialog from '../components/SigningAuditDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DocumentsPage = () => {
  const { token, user } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [previewDocument, setPreviewDocument] = useState(null);
  const [users, setUsers] = useState([]);
  const [sharePermission, setSharePermission] = useState('view');
  const [selectedUserId, setSelectedUserId] = useState('');
  const [documentAccess, setDocumentAccess] = useState([]);
  const [viewMode, setViewMode] = useState('own'); // 'own' or 'all'
  
  // Folder state
  const [folders, setFolders] = useState([]);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [createFolderOpen, setCreateFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [moveDocDialog, setMoveDocDialog] = useState(null);
  
  // Signature state
  const [signDialogDoc, setSignDialogDoc] = useState(null);
  const [signDocContent, setSignDocContent] = useState(null);
  const [docSignatures, setDocSignatures] = useState({});
  const [signatureFields, setSignatureFields] = useState([]);
  
  // Field editor state
  const [fieldEditorDoc, setFieldEditorDoc] = useState(null);
  const [fieldEditorContent, setFieldEditorContent] = useState(null);
  
  // Signing audit state
  const [auditDoc, setAuditDoc] = useState(null);
  
  // Delete confirmation state
  const [deleteConfirmDoc, setDeleteConfirmDoc] = useState(null);

  const isAdmin = user?.role === 'super_admin' || user?.role === 'admin' || user?.role === 'manager';

  const authHeaders = () => token ? { Authorization: `Bearer ${token}` } : {};

  const fetchDocuments = useCallback(async () => {
    try {
      const url = isAdmin && viewMode === 'all'
        ? `${API_URL}/api/documents?all_documents=true`
        : `${API_URL}/api/documents`;
      const response = await fetch(url, {
        credentials: 'include',
        headers: authHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setDocuments(data);
      }
    } catch (error) {
      console.error('Failed to fetch documents:', error);
    } finally {
      setLoading(false);
    }
  }, [token, isAdmin, viewMode]);

  const fetchFolders = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/folders`, {
        credentials: 'include',
        headers: authHeaders()
      });
      if (response.ok) setFolders(await response.json());
    } catch (error) {
      console.error('Failed to fetch folders:', error);
    }
  }, [token]);

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/users`, {
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const fetchDocumentAccess = async (documentId) => {
    try {
      const response = await fetch(`${API_URL}/api/documents/${documentId}/access`, {
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (response.ok) {
        const data = await response.json();
        setDocumentAccess(data);
      }
    } catch (error) {
      console.error('Failed to fetch document access:', error);
    }
  };

  useEffect(() => {
    fetchDocuments();
    fetchUsers();
    fetchFolders();
  }, [fetchDocuments, fetchFolders]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      // Use FileReader for broadest mobile browser support
      const base64Content = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        const timeout = setTimeout(() => { reader.abort(); reject(new Error('Read timed out')); }, 30000);
        reader.onload = () => {
          clearTimeout(timeout);
          try {
            const bytes = new Uint8Array(reader.result);
            const chunks = [];
            for (let i = 0; i < bytes.length; i += 4096) {
              chunks.push(String.fromCharCode.apply(null, bytes.subarray(i, Math.min(i + 4096, bytes.length))));
            }
            resolve(btoa(chunks.join('')));
          } catch (err) { reject(err); }
        };
        reader.onerror = () => { clearTimeout(timeout); reject(new Error('Read failed')); };
        reader.readAsArrayBuffer(file);
      });

      if (!base64Content || base64Content.length < 10) { toast.error('File appears to be empty'); setUploading(false); return; }

      // Chunked upload
      const CHUNK_SIZE = 150000;
      const totalChunks = Math.ceil(base64Content.length / CHUNK_SIZE);

      const initRes = await fetch(`${API_URL}/api/upload/init`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({
          filename: file.name,
          file_type: file.type || 'application/octet-stream',
          total_chunks: totalChunks,
          upload_type: 'document',
          metadata: {}
        })
      });
      if (!initRes.ok) { toast.error('Upload init failed'); setUploading(false); return; }
      const { upload_id } = await initRes.json();

      for (let i = 0; i < totalChunks; i++) {
        const chunk = base64Content.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
        const chunkRes = await fetch(`${API_URL}/api/upload/${upload_id}/chunk`, {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          body: JSON.stringify({ chunk_index: i, data: chunk })
        });
        if (!chunkRes.ok) { toast.error(`Chunk ${i + 1}/${totalChunks} failed`); setUploading(false); return; }
      }

      const completeRes = await fetch(`${API_URL}/api/upload/${upload_id}/complete`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) }
      });

      if (completeRes.ok) {
        toast.success('Document uploaded and encrypted!');
        setUploadDialogOpen(false);
        fetchDocuments();
      } else {
        const error = await completeRes.json();
        toast.error(error.detail || 'Upload failed');
      }
    } catch (error) {
      toast.error('Upload failed: ' + (error.message || 'Unknown error'));
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (doc) => {
    toast.info(`Preparing download: ${doc.name}...`);
    try {
      // Step 1: Try R2 presigned download URL (instant, browser handles attachment)
      const urlResp = await fetch(`${API_URL}/api/documents/${doc.document_id}/download-url`, {
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      if (urlResp.ok) {
        const data = await urlResp.json();
        if (data.url) {
          // Use a hidden anchor to trigger download without leaving the page
          const a = window.document.createElement('a');
          a.href = data.url;
          a.rel = 'noopener noreferrer';
          window.document.body.appendChild(a);
          a.click();
          a.remove();
          toast.success('Download started!');
          return;
        }
      }

      // Step 2: Fall back to legacy streaming download (MongoDB-stored docs)
      const response = await fetch(`${API_URL}/api/documents/${doc.document_id}/download`, {
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = window.document.createElement('a');
        a.href = url;
        a.download = doc.original_filename || doc.name;
        window.document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success('Download complete!');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Download failed');
      }
    } catch (error) {
      toast.error('Download failed. The file may be too large — try again.');
    }
  };

  const handleDelete = async (doc) => {
    setDeleteConfirmDoc(doc);
  };

  const confirmDelete = async () => {
    const doc = deleteConfirmDoc;
    if (!doc) return;
    setDeleteConfirmDoc(null);
    try {
      const response = await fetch(`${API_URL}/api/documents/${doc.document_id}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      if (response.ok) {
        toast.success('Document deleted!');
        fetchDocuments();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Delete failed');
      }
    } catch (error) {
      toast.error('Delete failed');
    }
  };

  const handleShare = async () => {
    if (!selectedUserId || !selectedDocument) return;

    try {
      const response = await fetch(`${API_URL}/api/documents/${selectedDocument.document_id}/access`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          user_id: selectedUserId,
          permission: sharePermission
        })
      });

      if (response.ok) {
        toast.success('Access granted!');
        fetchDocumentAccess(selectedDocument.document_id);
        setSelectedUserId('');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to grant access');
      }
    } catch (error) {
      toast.error('Failed to grant access');
    }
  };

  const handleRevokeAccess = async (userId) => {
    if (!selectedDocument) return;

    try {
      const response = await fetch(`${API_URL}/api/documents/${selectedDocument.document_id}/access/${userId}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      if (response.ok) {
        toast.success('Access revoked!');
        fetchDocumentAccess(selectedDocument.document_id);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to revoke access');
      }
    } catch (error) {
      toast.error('Failed to revoke access');
    }
  };

  const openShareDialog = (doc) => {
    setSelectedDocument(doc);
    fetchDocumentAccess(doc.document_id);
    setShareDialogOpen(true);
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    try {
      const response = await fetch(`${API_URL}/api/folders`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ name: newFolderName.trim() })
      });
      if (response.ok) {
        toast.success('Folder created!');
        setCreateFolderOpen(false);
        setNewFolderName('');
        fetchFolders();
      } else { const err = await response.json(); toast.error(err.detail || 'Failed'); }
    } catch { toast.error('Failed to create folder'); }
  };

  const handleDeleteFolder = async (folderId) => {
    if (!confirm('Delete this folder? Documents inside will be moved out.')) return;
    try {
      const response = await fetch(`${API_URL}/api/folders/${folderId}`, {
        method: 'DELETE', credentials: 'include', headers: authHeaders()
      });
      if (response.ok) {
        toast.success('Folder deleted');
        if (currentFolder === folderId) setCurrentFolder(null);
        fetchFolders();
        fetchDocuments();
      }
    } catch { toast.error('Failed to delete folder'); }
  };

  const handleMoveDocument = async (docId, folderId) => {
    try {
      const response = await fetch(`${API_URL}/api/documents/${docId}/move`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ folder_id: folderId })
      });
      if (response.ok) {
        toast.success('Document moved!');
        setMoveDocDialog(null);
        fetchDocuments();
      }
    } catch { toast.error('Failed to move document'); }
  };

  const handleSignDocument = async (docId, signatureData, position, metadata) => {
    try {
      const response = await fetch(`${API_URL}/api/documents/${docId}/sign`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({
          signature_data: signatureData,
          position: position || null,
          signer_typed_name: metadata?.signer_typed_name || null,
          signed_at_display: metadata?.signed_at_display || null,
          name_position: metadata?.name_position || null,
          sig_scale: metadata?.sig_scale || 1,
          name_scale: metadata?.name_scale || 1,
          field_values: metadata?.field_values || null
        })
      });
      if (response.ok) {
        toast.success('Document signed!');
        fetchSignatures(docId);
        // Refresh document content so preview/download includes the new signature
        try {
          const contentRes = await fetch(`${API_URL}/api/documents/${docId}/content`, {
            credentials: 'include', headers: authHeaders()
          });
          if (contentRes.ok) {
            const data = await contentRes.json();
            setSignDocContent(data);
          }
        } catch { /* ignore refresh error */ }
      } else { const err = await response.json(); toast.error(err.detail || 'Sign failed'); }
    } catch { toast.error('Failed to sign document'); }
  };

  const handleClearSignatures = async (doc) => {
    if (!window.confirm(`Clear ALL signatures from "${doc.name}"? This cannot be undone.`)) return;
    try {
      const response = await fetch(`${API_URL}/api/admin/documents/${doc.document_id}/signatures`, {
        method: 'DELETE', credentials: 'include', headers: authHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        setDocSignatures(prev => ({ ...prev, [doc.document_id]: [] }));
      } else { const err = await response.json(); toast.error(err.detail || 'Failed to clear signatures'); }
    } catch { toast.error('Failed to clear signatures'); }
  };


  const openSignDialog = async (doc) => {
    setSignatureFields([]);  // Reset before loading new doc fields
    setSignDialogDoc(doc);
    fetchSignatures(doc.document_id);
    // Fetch document content and signature fields in parallel
    try {
      const [contentRes, fieldsRes] = await Promise.all([
        fetch(`${API_URL}/api/documents/${doc.document_id}/content`, {
          credentials: 'include', headers: authHeaders()
        }),
        fetch(`${API_URL}/api/documents/${doc.document_id}/signature-fields`, {
          credentials: 'include', headers: authHeaders()
        })
      ]);
      if (contentRes.ok) {
        setSignDocContent(await contentRes.json());
      } else { setSignDocContent(null); }
      if (fieldsRes.ok) {
        const data = await fieldsRes.json();
        setSignatureFields(data.fields || []);
      } else { setSignatureFields([]); }
    } catch { setSignDocContent(null); setSignatureFields([]); }
  };

  const openFieldEditor = async (doc) => {
    setFieldEditorDoc(doc);
    try {
      const response = await fetch(`${API_URL}/api/documents/${doc.document_id}/content`, {
        credentials: 'include', headers: authHeaders()
      });
      if (response.ok) {
        setFieldEditorContent(await response.json());
      } else { setFieldEditorContent(null); }
    } catch { setFieldEditorContent(null); }
  };

  const fetchSignatures = async (docId) => {
    try {
      const response = await fetch(`${API_URL}/api/documents/${docId}/signatures`, {
        credentials: 'include', headers: authHeaders()
      });
      if (response.ok) {
        const sigs = await response.json();
        setDocSignatures(prev => ({ ...prev, [docId]: sigs }));
      }
    } catch { /* ignore */ }
  };

  const openPreview = (doc) => {
    setPreviewDocument(doc);
  };

  const closePreview = () => {
    setPreviewDocument(null);
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString();
  };

  const getFileIcon = (fileType) => {
    if (fileType?.includes('pdf')) return '📄';
    if (fileType?.includes('image')) return '🖼️';
    if (fileType?.includes('word') || fileType?.includes('document')) return '📝';
    if (fileType?.includes('excel') || fileType?.includes('spreadsheet')) return '📊';
    return '📁';
  };

  const filteredDocuments = documents.filter(doc => {
    const matchesSearch = doc.name.toLowerCase().includes(searchQuery.toLowerCase());
    if (currentFolder) {
      return matchesSearch && doc.folder_id === currentFolder;
    }
    return matchesSearch && !doc.folder_id;
  });

  return (
    <DashboardLayout title={isAdmin && viewMode === 'all' ? "All Documents" : "My Documents"}>
      <div className="space-y-6">
        {/* Header Actions */}
        <div className="flex flex-col sm:flex-row gap-4 justify-between">
          <div className="flex items-center gap-3 flex-1">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
              <Input
                placeholder="Search documents..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-black/50 border-white/10 text-white placeholder:text-white/30"
                data-testid="search-documents-input"
              />
            </div>
            {isAdmin && (
              <div className="flex rounded-sm overflow-hidden border border-white/10" data-testid="doc-view-toggle">
                <button
                  onClick={() => setViewMode('own')}
                  className={`px-3 py-2 text-xs font-mono uppercase tracking-wider transition-colors ${viewMode === 'own' ? 'bg-primary text-black' : 'bg-black/40 text-white/60 hover:text-white'}`}
                  data-testid="view-own-docs-btn"
                >
                  My Docs
                </button>
                <button
                  onClick={() => setViewMode('all')}
                  className={`px-3 py-2 text-xs font-mono uppercase tracking-wider transition-colors ${viewMode === 'all' ? 'bg-primary text-black' : 'bg-black/40 text-white/60 hover:text-white'}`}
                  data-testid="view-all-docs-btn"
                >
                  All Users
                </button>
              </div>
            )}
          </div>
          
          <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-primary text-black hover:bg-primary/90 font-mono uppercase tracking-wider btn-glow" data-testid="upload-document-btn">
                <Upload className="w-4 h-4 mr-2" />
                Upload Document
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#0A0A0A] border-white/10">
              <DialogHeader>
                <DialogTitle className="text-white" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  Upload Document
                </DialogTitle>
                <DialogDescription className="text-white/60">
                  Your document will be encrypted with AES-256 before storage.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="border-2 border-dashed border-white/20 rounded-sm p-8 text-center hover:border-primary/50 transition-colors">
                  <input
                    type="file"
                    onChange={handleUpload}
                    className="hidden"
                    id="file-upload"
                    disabled={uploading}
                    data-testid="file-upload-input"
                  />
                  <label htmlFor="file-upload" className="cursor-pointer">
                    <Upload className="w-12 h-12 text-white/30 mx-auto mb-4" />
                    <p className="text-white mb-2">
                      {uploading ? 'Encrypting & Uploading...' : 'Click to upload or drag and drop'}
                    </p>
                    <p className="text-white/40 text-sm">
                      PDF, Word, Excel, Images up to 50MB
                    </p>
                  </label>
                </div>
                <div className="flex items-center gap-2 text-xs text-primary">
                  <Lock className="w-3 h-3" />
                  <span>End-to-end encrypted with your personal key</span>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Folders Bar */}
        {viewMode === 'own' && (
          <div className="flex items-center gap-3 flex-wrap" data-testid="folders-bar">
            {currentFolder && (
              <button
                onClick={() => setCurrentFolder(null)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-white/60 hover:text-white bg-black/40 border border-white/10 rounded-sm transition-colors"
                data-testid="back-to-root-btn"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                All Files
              </button>
            )}
            {!currentFolder && folders.map(folder => (
              <div
                key={folder.folder_id}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-white/80 hover:text-white bg-black/40 border border-white/10 hover:border-primary/30 rounded-sm transition-colors group"
                data-testid={`folder-${folder.folder_id}`}
              >
                <button onClick={() => setCurrentFolder(folder.folder_id)} className="flex items-center gap-1.5 flex-1">
                  <Folder className="w-3.5 h-3.5 text-primary" />
                  {folder.name}
                  <span className="text-white/30 text-xs">({folder.document_count})</span>
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); handleDeleteFolder(folder.folder_id); }}
                  className="ml-1 opacity-0 group-hover:opacity-100 text-red-400/60 hover:text-red-400 transition-opacity"
                  data-testid={`delete-folder-${folder.folder_id}`}
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            ))}
            {!currentFolder && (
              <button
                onClick={() => setCreateFolderOpen(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-primary/70 hover:text-primary border border-dashed border-primary/30 hover:border-primary rounded-sm transition-colors"
                data-testid="create-folder-btn"
              >
                <FolderPlus className="w-3.5 h-3.5" />
                New Folder
              </button>
            )}
            {currentFolder && (
              <div className="flex items-center gap-1.5 text-sm text-primary">
                <FolderOpen className="w-4 h-4" />
                <span className="font-medium">{folders.find(f => f.folder_id === currentFolder)?.name}</span>
              </div>
            )}
          </div>
        )}

        {/* Documents Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="animate-pulse bg-black/40 border border-white/10 rounded-sm p-6 h-40" />
            ))}
          </div>
        ) : filteredDocuments.length === 0 ? (
          <div className="text-center py-16">
            <FileText className="w-16 h-16 text-white/20 mx-auto mb-4" />
            <h3 className="text-xl text-white mb-2">No Documents Yet</h3>
            <p className="text-white/60 mb-6">Upload your first document to get started</p>
            <Button 
              onClick={() => setUploadDialogOpen(true)}
              className="bg-primary text-black hover:bg-primary/90"
              data-testid="empty-state-upload-btn"
            >
              <Upload className="w-4 h-4 mr-2" />
              Upload Document
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredDocuments.map((doc) => (
              <div 
                key={doc.document_id}
                className="bg-black/40 backdrop-blur-xl border border-white/10 rounded-sm p-6 hover:border-primary/30 transition-all card-hover group"
                data-testid={`document-card-${doc.document_id}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <span className="text-3xl">{getFileIcon(doc.file_type)}</span>
                    <div>
                      <h3 className="text-white font-medium truncate max-w-[180px]" title={doc.name}>
                        {doc.name}
                      </h3>
                      <p className="text-white/40 text-xs font-mono">
                        {formatFileSize(doc.file_size)}
                      </p>
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`document-menu-${doc.document_id}`}>
                        <MoreVertical className="w-4 h-4 text-white/60" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="bg-[#0A0A0A] border-white/10">
                      <DropdownMenuItem 
                        onClick={() => openPreview(doc)}
                        className="text-white hover:bg-white/5 cursor-pointer"
                        data-testid={`preview-${doc.document_id}`}
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        Preview
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={() => handleDownload(doc)}
                        className="text-white hover:bg-white/5 cursor-pointer"
                        data-testid={`download-${doc.document_id}`}
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Download
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={() => openShareDialog(doc)}
                        className="text-white hover:bg-white/5 cursor-pointer"
                        data-testid={`share-${doc.document_id}`}
                      >
                        <Share2 className="w-4 h-4 mr-2" />
                        Share
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={() => { openSignDialog(doc); }}
                        className="text-white hover:bg-white/5 cursor-pointer"
                        data-testid={`sign-${doc.document_id}`}
                      >
                        <PenTool className="w-4 h-4 mr-2" />
                        Sign
                      </DropdownMenuItem>
                      {(isAdmin || doc.is_owner) && (
                        <DropdownMenuItem 
                          onClick={() => openFieldEditor(doc)}
                          className="text-white hover:bg-white/5 cursor-pointer"
                          data-testid={`manage-fields-${doc.document_id}`}
                        >
                          <LayoutTemplate className="w-4 h-4 mr-2" />
                          Manage Sign Fields
                        </DropdownMenuItem>
                      )}
                      {(isAdmin || doc.is_owner) && (
                        <DropdownMenuItem 
                          onClick={() => setAuditDoc(doc)}
                          className="text-white hover:bg-white/5 cursor-pointer"
                          data-testid={`signing-history-${doc.document_id}`}
                        >
                          <Clock className="w-4 h-4 mr-2" />
                          Signing History
                        </DropdownMenuItem>
                      )}
                      {viewMode === 'own' && (
                        <DropdownMenuItem 
                          onClick={() => setMoveDocDialog(doc)}
                          className="text-white hover:bg-white/5 cursor-pointer"
                          data-testid={`move-${doc.document_id}`}
                        >
                          <Move className="w-4 h-4 mr-2" />
                          Move to Folder
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuSeparator className="bg-white/10" />
                      {user?.role === 'super_admin' && docSignatures[doc.document_id]?.length > 0 && (
                        <DropdownMenuItem 
                          onClick={() => handleClearSignatures(doc)}
                          className="text-amber-400 hover:bg-amber-400/10 cursor-pointer"
                          data-testid={`clear-sigs-${doc.document_id}`}
                        >
                          <Eraser className="w-4 h-4 mr-2" />
                          Clear Signatures
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem 
                        onSelect={() => { setTimeout(() => setDeleteConfirmDoc(doc), 0); }}
                        className="text-red-400 hover:bg-red-400/10 cursor-pointer"
                        data-testid={`delete-${doc.document_id}`}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                <div className="space-y-2">
                  {doc.description && (
                    <p className="text-white/60 text-sm truncate">{doc.description}</p>
                  )}
                  {isAdmin && viewMode === 'all' && doc.owner_name && !doc.is_owner && (
                    <p className="text-xs text-secondary/80 flex items-center gap-1" data-testid={`doc-owner-${doc.document_id}`}>
                      <Users className="w-3 h-3" /> {doc.owner_name}
                    </p>
                  )}
                  {docSignatures[doc.document_id]?.length > 0 && (
                    <div className="flex items-center gap-1 text-xs text-primary/80">
                      <PenTool className="w-3 h-3" />
                      <span>{docSignatures[doc.document_id].length} signature{docSignatures[doc.document_id].length > 1 ? 's' : ''}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-white/40">{formatDate(doc.created_at)}</span>
                    <span className="text-primary flex items-center gap-1">
                      <Lock className="w-3 h-3" />
                      Encrypted
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Share Dialog */}
        <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
          <DialogContent className="bg-[#0A0A0A] border-white/10 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-white" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Share Document
              </DialogTitle>
              <DialogDescription className="text-white/60">
                Grant access to "{selectedDocument?.name}"
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 mt-4">
              {/* Add User */}
              <div className="space-y-3">
                <Label className="text-white/80">Add User</Label>
                <div className="flex gap-2">
                  <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                    <SelectTrigger className="flex-1 bg-black/50 border-white/10 text-white">
                      <SelectValue placeholder="Select user" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border-white/10">
                      {users.filter(u => u.user_id !== selectedDocument?.owner_id).map((user) => (
                        <SelectItem key={user.user_id} value={user.user_id} className="text-white">
                          {user.name} ({user.email})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={sharePermission} onValueChange={setSharePermission}>
                    <SelectTrigger className="w-32 bg-black/50 border-white/10 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border-white/10">
                      <SelectItem value="view" className="text-white">
                        <div className="flex items-center gap-2">
                          <Eye className="w-4 h-4" />
                          View
                        </div>
                      </SelectItem>
                      <SelectItem value="edit" className="text-white">
                        <div className="flex items-center gap-2">
                          <Edit className="w-4 h-4" />
                          Edit
                        </div>
                      </SelectItem>
                      <SelectItem value="download" className="text-white">
                        <div className="flex items-center gap-2">
                          <Download className="w-4 h-4" />
                          Download
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button 
                  onClick={handleShare}
                  disabled={!selectedUserId}
                  className="w-full bg-primary text-black hover:bg-primary/90"
                  data-testid="grant-access-btn"
                >
                  Grant Access
                </Button>
              </div>

              {/* Current Access */}
              <div className="space-y-3">
                <Label className="text-white/80">Current Access</Label>
                {documentAccess.length === 0 ? (
                  <p className="text-white/40 text-sm text-center py-4">
                    No one else has access to this document
                  </p>
                ) : (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {documentAccess.map((access) => (
                      <div 
                        key={access.access_id}
                        className="flex items-center justify-between p-3 bg-white/5 rounded-sm"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-secondary/20 flex items-center justify-center text-secondary text-sm font-semibold">
                            {access.user_name?.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <p className="text-white text-sm">{access.user_name}</p>
                            <p className="text-white/40 text-xs">{access.permission}</p>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRevokeAccess(access.user_id)}
                          className="text-red-400 hover:bg-red-400/10"
                          data-testid={`revoke-access-${access.user_id}`}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Document Preview Modal */}
        {previewDocument && (
          <DocumentPreview
            document={previewDocument}
            token={token}
            onClose={closePreview}
            onDownload={handleDownload}
          />
        )}

        {/* Create Folder Dialog */}
        <Dialog open={createFolderOpen} onOpenChange={setCreateFolderOpen}>
          <DialogContent className="bg-[#0A0A0A] border-white/10 max-w-sm">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <FolderPlus className="w-5 h-5 text-primary" />
                Create Folder
              </DialogTitle>
              <DialogDescription className="text-white/60">
                Organize your documents into groups
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label className="text-white/80">Folder Name</Label>
                <Input
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  placeholder="e.g. Contracts, Reports..."
                  className="mt-1 bg-black/50 border-white/10 text-white"
                  data-testid="folder-name-input"
                  onKeyDown={(e) => e.key === 'Enter' && handleCreateFolder()}
                />
              </div>
              <Button onClick={handleCreateFolder} disabled={!newFolderName.trim()} className="w-full bg-primary text-black hover:bg-primary/90" data-testid="confirm-create-folder-btn">
                Create Folder
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Move to Folder Dialog */}
        <Dialog open={!!moveDocDialog} onOpenChange={(v) => !v && setMoveDocDialog(null)}>
          <DialogContent className="bg-[#0A0A0A] border-white/10 max-w-sm">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <Move className="w-5 h-5 text-primary" />
                Move Document
              </DialogTitle>
              <DialogDescription className="text-white/60">
                Move "{moveDocDialog?.name}" to a folder
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2 mt-4">
              <button
                onClick={() => handleMoveDocument(moveDocDialog?.document_id, null)}
                className="w-full p-3 text-left rounded-sm border border-white/10 hover:border-primary/30 text-white/80 hover:text-white transition-colors flex items-center gap-2"
                data-testid="move-to-root"
              >
                <FileText className="w-4 h-4 text-white/40" />
                Root (no folder)
              </button>
              {folders.map(folder => (
                <button
                  key={folder.folder_id}
                  onClick={() => handleMoveDocument(moveDocDialog?.document_id, folder.folder_id)}
                  className={`w-full p-3 text-left rounded-sm border transition-colors flex items-center gap-2 ${moveDocDialog?.folder_id === folder.folder_id ? 'border-primary/50 bg-primary/10 text-primary' : 'border-white/10 hover:border-primary/30 text-white/80 hover:text-white'}`}
                  data-testid={`move-to-${folder.folder_id}`}
                >
                  <Folder className="w-4 h-4 text-primary" />
                  {folder.name}
                </button>
              ))}
              {folders.length === 0 && (
                <p className="text-white/40 text-sm text-center py-4">No folders yet. Create one first.</p>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Sign Document Dialog - with document preview */}
        <DocumentSignerDialog
          open={!!signDialogDoc}
          onOpenChange={(v) => { if (!v) { setSignDialogDoc(null); setSignDocContent(null); setSignatureFields([]); } }}
          documentContent={signDocContent?.content || null}
          documentType={signDocContent?.file_type || signDialogDoc?.file_type || ''}
          documentName={signDialogDoc?.name || ''}
          existingSignatures={docSignatures[signDialogDoc?.document_id] || []}
          onSign={(sigData, position, metadata) => handleSignDocument(signDialogDoc?.document_id, sigData, position, metadata)}
          signatureFields={signatureFields}
          currentUserId={user?.user_id}
        />

        {/* Signature Field Template Editor */}
        <SignatureFieldEditor
          open={!!fieldEditorDoc}
          onOpenChange={(v) => { if (!v) { setFieldEditorDoc(null); setFieldEditorContent(null); } }}
          documentId={fieldEditorDoc?.document_id}
          documentContent={fieldEditorContent?.content || null}
          documentType={fieldEditorContent?.file_type || fieldEditorDoc?.file_type || ''}
          documentName={fieldEditorDoc?.name || ''}
          token={token}
        />

        {/* Signing Audit Trail Dialog */}
        <SigningAuditDialog
          open={!!auditDoc}
          onOpenChange={(v) => { if (!v) setAuditDoc(null); }}
          documentId={auditDoc?.document_id}
          documentName={auditDoc?.name || ''}
          token={token}
        />

        {/* View Signatures Panel */}
        {signDialogDoc && !signDialogDoc && docSignatures[signDialogDoc?.document_id]?.length > 0 && (
          <div className="fixed bottom-4 right-4 z-50 bg-[#0A0A0A] border border-white/10 rounded-sm p-4 w-72 shadow-xl" data-testid="signatures-panel">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-white text-sm font-medium flex items-center gap-1.5">
                <PenTool className="w-3.5 h-3.5 text-primary" />
                Signatures ({docSignatures[signDialogDoc.document_id].length})
              </h4>
              <button onClick={() => setSignDialogDoc(null)} className="text-white/40 hover:text-white">
                <X className="w-4 h-4" />
              </button>
            </div>
            <SignatureDisplay signatures={docSignatures[signDialogDoc.document_id]} />
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={!!deleteConfirmDoc} onOpenChange={(v) => { if (!v) setDeleteConfirmDoc(null); }}>
          <AlertDialogContent className="bg-[#0A0A0A] border-white/10 max-w-md">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-white flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-red-400" />
                Delete Document
              </AlertDialogTitle>
              <AlertDialogDescription className="text-white/60">
                Are you sure you want to delete <strong className="text-white">{deleteConfirmDoc?.name}</strong>? This will also remove all signatures and field templates. This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="border-white/10 text-white hover:bg-white/5" data-testid="delete-cancel-btn">
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction onClick={confirmDelete}
                className="bg-red-600 text-white hover:bg-red-700" data-testid="delete-confirm-btn">
                <Trash2 className="w-4 h-4 mr-2" /> Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
};

export default DocumentsPage;
